import { CardGridLayout, GridInput } from 'lib/components/Card/Grid';
import CheckboxGroup from 'lib/components/Checkbox/CheckboxGroup';
import { FilingType, isOrderFilingType } from 'lib/types/filingType';
import { usePublisherModularSizes } from 'routes/placeScroll/hooks/usePublisherModularSizes';
import { useAppSelector } from 'redux/hooks';
import { selectActiveOrganization } from 'redux/auth';

type FilingTypeModularSizesSettingsProps<T extends FilingType> = {
  updatedFilingType: T;
  onUpdateFilingType: (filingType: T) => void;
};

export default function FilingTypeModularSizesSettings<T extends FilingType>({
  updatedFilingType,
  onUpdateFilingType
}: FilingTypeModularSizesSettingsProps<T>) {
  const activeOrganization = useAppSelector(selectActiveOrganization);

  const { modularSizes: availableModularSizes, loading: modularSizesLoading } =
    usePublisherModularSizes(activeOrganization?.ref);

  if (!isOrderFilingType(updatedFilingType)) {
    return null;
  }

  if (!activeOrganization) {
    return null;
  }

  const { modularSizes = [] } = updatedFilingType;

  return (
    <CardGridLayout
      header={{
        title: 'Modular Sizes',
        description:
          'Select which modular sizes are available for this filing type'
      }}
    >
      <GridInput fullWidth>
        {modularSizesLoading && <div>Loading modular sizes...</div>}

        {!modularSizesLoading && availableModularSizes.length > 0 && (
          <CheckboxGroup
            id="supported-modular-sizes"
            labelText="Supported modular sizes"
            options={availableModularSizes.map(modularSize => {
              return {
                id: modularSize.id,
                labelText: modularSize.data().name,
                labelDescription: `${modularSize.data().width}" x ${
                  modularSize.data().height
                }"`,
                checked: modularSizes.some(ref => ref.id === modularSize.id),
                value: modularSize.id
              };
            })}
            type="checkbox"
            onChange={newModularSizes => {
              const selectedModularSizes = newModularSizes.filter(
                modularSize => modularSize.checked
              );
              onUpdateFilingType({
                ...updatedFilingType,
                modularSizes: selectedModularSizes.map(
                  modularSize =>
                    availableModularSizes.find(ms => ms.id === modularSize.id)!
                      .ref
                )
              });
            }}
          />
        )}

        {!modularSizesLoading && availableModularSizes.length === 0 && (
          <div className="text-gray-500">
            No modular sizes available. Create modular sizes in the billing
            settings to enable them for this filing type.
          </div>
        )}
      </GridInput>
    </CardGridLayout>
  );
}
